/* GASSISA
=========================================================================================
* Descripción: Modal para generar apartados desde Venta Super. Cabecero con datos del
*              cliente + pago global (tipo de pago / efectivo, tal cual el modal de
*              cobro de abonos de apartados) y tabla de partidas con columna de abono
*              por renglón. El pago global es informativo para el ticket; la
*              persistencia de abonos se hace por partida en AbonosApartados.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Navegación controlada de cabeceros y abonos
*      3       | 14/10/2026 | Refactorización                  | Cabecero de pago global estilo cobro de
*              |            |                                  | abonos (tipos de pago, atajos Alt+E/C/D/Q/V/T,
*              |            |                                  | Alt+Fin para generar, Escape cancela); tabla
*              |            |                                  | simple sin paginador; keys estables por renglón
*      4       | 14/10/2026 | Refactorización                  | Bloqueo de pago total: solo primer abono
*              |            |                                  | parcial o sin abono; la entrega definitiva
*              |            |                                  | se hace en FrmApartados
*      5       | 14/10/2026 | Refactorización                  | Navegación con flechas en línea de
*              |            |                                  | captura (izq/der entre campos, abajo a
*              |            |                                  | tabla) con focus+select en cada salto
*      6       | 14/10/2026 | Refactorización                  | Auto-distribución solo sin edición manual:
*              |            |                                  | el cliente decide montos por renglón y el
*              |            |                                  | capital solo debe cuadrar con la columna
*      7       | 14/10/2026 | Refactorización                  | Dispersión siempre activa al escribir el
*              |            |                                  | global (incluye 1 solo renglón con sus
*              |            |                                  | decimales); captura manual por orden de
*              |            |                                  | operación: global, ajuste por renglón, fin
* --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { apiConceptosYGruposList } from "../../settings/ApiConfig"
import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { NumberFormat$ } from "../../settings/utils";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";

import React, { useState, useEffect, useRef } from "react";
import { NumericFormat } from 'react-number-format';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from "@mui/icons-material/PersonOutlineSharp";
import { AttachMoney, CreditCard, AccountBalance, AccountBalanceWallet, CallToAction } from '@mui/icons-material';

import { Button, Modal, Box, MenuItem, Typography, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";

// Estilos para el modal con efecto suavizado de desplazamiento
const modalStyle = {
    position: 'fixed',
    top: 'calc(9% - 1.5cm)',
    right: '5%',
    width: '90vw',
    height: 'calc(78vh + 2cm)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(100%)', // Inicialmente fuera de la pantalla
    transition: 'transform 0.5s ease-in-out',
    borderRadius: '9px',
    overflowY: 'hidden',
};

const TIPOS_PAGO = ["EFECTIVO", "CHEQUE", "VALES", "TRANSFERENCIA", "TARJETA DÉBITO", "TARJETA CRÉDITO"];
const TOLERANCIA = 0.01;

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

function NumericFormatCustom(props) {
    const { inputRef, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value, // Valor numérico sin formato.
                    },
                });
            }}
            thousandSeparator
            decimalSeparator="."
            prefix="$"
            decimalScale={2}
        />
    );
}

const getPagoInicial = () => ({
    tipoPago: "EFECTIVO",
    montoTipoPago: 0,
    montoEfectivo: 0,
    digitosTarjeta: "",
    datosCheque: "",
    datosVale: "",
    datosTransferencia: "",
});

export default function ModalGenerarApartados(props) {
    const { openModalJalarPreventaCotizacion = false, handleCloseModalGenerarApartados, handleGenerarApartado, listaVentas } = props;

    const { GetRequest } = useRequest()
    const { handleOpenAlert } = useLayout();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const [modalVisible, setModalVisible] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const [filas, setFilas] = useState([]);
    const [abonos, setAbonos] = useState({});

    const [detalles, setDetalles] = useState({
        concepto: null,
        grupo: null,
        nombrecliente: "",
        telefonocliente: ""
    });

    const [inputConcepto, setInputConcepto] = useState("");
    const [inputGrupo, setInputGrupo] = useState("");

    const [listaConceptos, setListaConceptos] = useState([{
        concepto: "",
        grupo: "",
    }]);

    const [listaConceptosAgrupados, setListaConceptosAgrupados] = useState([]);
    const [listaGrupos, setListaGrupos] = useState([]);
    const [pago, setPago] = useState(getPagoInicial());
    // Marca si ya se intentó generar: activa los errores visibles por campo.
    const [intentoEnviar, setIntentoEnviar] = useState(false);

    const conceptoInputRef = useRef(null);
    const grupoInputRef = useRef(null);
    const nombreClienteInputRef = useRef(null);
    const telefonoClienteInputRef = useRef(null);
    const abonoInputRefs = useRef({});
    const submitButtonRef = useRef(null);
    const submitFnRef = useRef(null);

    // ---------- Totales ----------
    const totalCuenta = round2(filas.reduce((acc, f) => acc + (Number(f.totalPartida) || 0), 0));
    const totalColumnaAbonos = round2(Object.values(abonos).reduce((acc, v) => acc + (Number(v) || 0), 0));
    const totalCapitalRecibido = round2((Number(pago.montoEfectivo) || 0) + (Number(pago.montoTipoPago) || 0));
    const diferencia = round2(totalCuenta - totalColumnaAbonos);

    const conceptoOk = ((detalles.concepto || inputConcepto || "").trim().length > 0);
    const nombreOk = ((detalles.nombrecliente || "").trim().length > 0);
    const cuadreOk = Math.abs(totalColumnaAbonos - totalCapitalRecibido) <= TOLERANCIA;
    const topeOk = totalColumnaAbonos <= totalCuenta + TOLERANCIA;
    // En este modal no hay entrega definitiva: el anticipo nunca puede cubrir
    // el 100% de la cuenta. Solo primer abono parcial o apartado sin abonos.
    // La liquidación total se hace en el módulo de Apartados.
    const bloqueaPagoTotal = totalCuenta > 0 && totalColumnaAbonos > 0 && diferencia <= TOLERANCIA;
    const validoParaGenerar = conceptoOk && nombreOk && cuadreOk && topeOk && filas.length > 0 && !bloqueaPagoTotal;

    const focusAndSelect = (inputRef, delay = 0) => {
        setTimeout(() => {
            const input = inputRef?.current;
            if (!input) return;
            input.focus();
            input.select?.();
        }, delay);
    };

    const focusAbonoByKey = (rowKey, delay = 0) => {
        setTimeout(() => {
            const input = abonoInputRefs.current[rowKey];
            if (!input) return;
            input.focus();
            input.select?.();
        }, delay);
    };

    useEffect(() => {
        if (openModalJalarPreventaCotizacion) {
            const base = Array.isArray(listaVentas) ? listaVentas : [];
            const filasX = base.map((r, idx) => {
                const cantidad = Number(r.cantidad) || 0;
                const costo = Number(r.costocliente ?? r.costounitario) || 0;
                const totalPartida = round2(Number(r.total) || (cantidad * costo));
                return {
                    ...r,
                    rowKey: `${r.idpartida ?? "P"}@@${r.foliopreventa ?? ""}@@${r.clavearticulo ?? ""}@@${idx}`,
                    totalPartida,
                };
            });
            setFilas(filasX);
            setAbonos({});
            setPago(getPagoInicial());
            setDetalles({ concepto: null, grupo: null, nombrecliente: "", telefonocliente: "" });
            setInputConcepto("");
            setInputGrupo("");
            setIntentoEnviar(false);
            setInternalOpen(true);
            setTimeout(() => setModalVisible(true), 50);
            focusAndSelect(conceptoInputRef, 180);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 500);
        }
        // Se construye solo al abrir para no perder lo capturado con re-renders del padre.
    }, [openModalJalarPreventaCotizacion]);

    const modalVisibleStyle = {
        transform: modalVisible ? 'translateX(0)' : 'translateX(100%)',
    };

    const handleAccionarRegistroApartado = async () => {
        if (isProcessing) return;
        // Bloqueo en firme: sin concepto y/o sin nombre no hay registro.
        // Teléfono y grupo son opcionales.
        setIntentoEnviar(true);

        if (!conceptoOk) {
            handleOpenAlert("Debe seleccionar o ingresar un concepto de apartado", "warning");
            focusAndSelect(conceptoInputRef);
            return;
        }

        if (!nombreOk) {
            handleOpenAlert("Debe ingresar el nombre del cliente", "warning");
            focusAndSelect(nombreClienteInputRef);
            return;
        }

        if (!cuadreOk) {
            handleOpenAlert(`El total de la columna de abonos (${NumberFormat$(totalColumnaAbonos)}) debe cuadrar con el capital recibido (${NumberFormat$(totalCapitalRecibido)}). Use "Distribuir pago" o ajuste los montos.`, "warning");
            return;
        }

        if (!topeOk) {
            handleOpenAlert("El total de abonos supera el total de la cuenta", "warning");
            return;
        }

        if (bloqueaPagoTotal) {
            handleOpenAlert("El anticipo no puede cubrir el 100% de la cuenta. Solo se recibe primer abono parcial o apartado sin abonos; la liquidación total se hace en el módulo de Apartados.", "warning");
            return;
        }

        executeWithProtection(async () => {
            const filasConAbono = filas.map((f) => ({
                ...f,
                montoAbono: round2(abonos[f.rowKey] || 0),
            }));
            const serial = ((pago.datosTransferencia || "") + (pago.datosCheque || "") + (pago.datosVale || "") + (pago.digitosTarjeta || "")).trim();
            const res = await handleGenerarApartado(
                {
                    concepto: (detalles.concepto || inputConcepto || "").trim(),
                    grupo: (detalles.grupo || inputGrupo || "").trim(),
                    nombrecliente: (detalles.nombrecliente || "").trim(),
                    telefonocliente: (detalles.telefonocliente || "").trim(),
                },
                filasConAbono,
                {
                    tipoPago: (pago.tipoPago || "EFECTIVO").trim(),
                    montoEfectivo: round2(pago.montoEfectivo),
                    montoTipoPago: round2(pago.montoTipoPago),
                    serial,
                }
            );
            if (res && res.ok) {
                handleCloseModalGenerarApartados();
            }
        });
    };
    submitFnRef.current = handleAccionarRegistroApartado;

    const handleChangeDetalles = (e) => {
        if (e.target.value === null)
            return;

        setDetalles(prevDetalles => ({
            ...prevDetalles,
            [e.target.name]: e.target.value
        }));
    };

    const handleEditMontoAbono = (rowKey, value) => {
        // Se guarda el texto crudo para no interrumpir la captura (el formato
        // "$" reescribe el input si se normaliza en cada tecla) y así el
        // retotalizado es inmediato. La normalización y topes van en onBlur
        // y en la validación del submit. Lo capturado a mano por renglón
        // (artículo + medida, incluido cero) se respeta hasta que se vuelva
        // a escribir en el total global.
        setAbonos((prev) => ({ ...prev, [rowKey]: value ?? "" }));
    };

    const handleNormalizarMontoAbono = (rowKey, topePartida) => {
        const tope = Number(topePartida) || 0;
        setAbonos((prev) => {
            const actual = prev[rowKey];
            if (actual === undefined || actual === null || String(actual).trim() === "") {
                return { ...prev, [rowKey]: 0 };
            }
            const num = Number(actual) || 0;
            return { ...prev, [rowKey]: Math.min(Math.max(num, 0), tope) };
        });
    };

    const handleChangeTipoPago = (tipox) => {
        // Tal cual el modal de cobro de abonos: cambiar el tipo de pago resume
        // montos y columna de abonos, y enfoca el insumo correspondiente.
        setPago((prev) => ({
            ...prev,
            tipoPago: tipox,
            montoTipoPago: 0,
            montoEfectivo: 0,
            digitosTarjeta: "",
            datosCheque: "",
            datosVale: "",
            datosTransferencia: "",
        }));
        setAbonos({});
        setTimeout(() => {
            if (tipox === "EFECTIVO") {
                focusAndSelect(efectivoInputRef);
            } else if (tipox === "TARJETA DÉBITO" || tipox === "TARJETA CRÉDITO") {
                focusAndSelect(digitosInputRef);
            } else if (tipox === "TRANSFERENCIA") {
                focusAndSelect(transferenciaInputRef);
            } else if (tipox === "CHEQUE") {
                focusAndSelect(chequeInputRef);
            } else if (tipox === "VALES") {
                focusAndSelect(valesInputRef);
            } else {
                focusAndSelect(otroMontoInputRef);
            }
        }, 50);
    };

    // Distribución uniforme del capital global entre las partidas: montos
    // enteros para todos los renglones y el último recibe el resto
    // (incluidos los decimales faltantes). Si el apartado es de un solo
    // artículo, todo el monto con sus decimales queda en esa única fila.
    // Respeta el tope por partida.
    const distribuirUniforme = (capital, lista) => {
        const nuevo = {};
        const n = lista.length;
        if (n === 0) return nuevo;
        const total = round2(capital);
        if (n === 1) {
            const tope = Number(lista[0].totalPartida) || 0;
            nuevo[lista[0].rowKey] = round2(Math.min(Math.max(total, 0), tope));
            return nuevo;
        }
        let restante = total;
        const base = total > 0 ? Math.floor(total / n) : 0;
        lista.forEach((f, idx) => {
            const tope = Number(f.totalPartida) || 0;
            let monto = 0;
            if (restante > 0) {
                monto = idx === n - 1 ? restante : Math.min(base, restante);
                monto = Math.min(monto, tope);
            }
            nuevo[f.rowKey] = round2(monto);
            restante = round2(restante - monto);
        });
        return nuevo;
    };

    const handleChangeMontoPago = (campo) => (e) => {
        // Texto crudo igual que los abonos: retotaliza de inmediato sin
        // reescribir lo que se está tecleando.
        const crudo = e.target.value ?? "";
        const num = Number(crudo) || 0;
        setPago((prev) => ({ ...prev, [campo]: crudo }));
        // Escribir en el total global (tipo pago y/o efectivo, que operan en
        // paralelo) siempre dispara la dispersión uniforme entre los
        // renglones: enteros para todos y el último recibe la diferencia con
        // decimales. Con un solo renglón, todo el monto (con decimales) queda
        // en esa única fila. La captura manual por renglón se hace después y
        // se respeta hasta la próxima escritura del global.
        const capital = round2((campo === "montoEfectivo" ? num : Number(pago.montoEfectivo) || 0) +
            (campo === "montoTipoPago" ? num : Number(pago.montoTipoPago) || 0));
        setAbonos(distribuirUniforme(capital, filas));
    };

    const efectivoInputRef = useRef(null);
    const otroMontoInputRef = useRef(null);
    const tipoPagoInputRef = useRef(null);
    const digitosInputRef = useRef(null);
    const transferenciaInputRef = useRef(null);
    const chequeInputRef = useRef(null);
    const valesInputRef = useRef(null);

    // Ref del insumo de serie visible según el tipo de pago (null si EFECTIVO).
    const refSerialVisible = () => {
        const t = pago.tipoPago;
        if (t === 'TARJETA DÉBITO' || t === 'TARJETA CRÉDITO') return digitosInputRef;
        if (t === 'CHEQUE') return chequeInputRef;
        if (t === 'VALES') return valesInputRef;
        if (t === 'TRANSFERENCIA') return transferenciaInputRef;
        return null;
    };

    const lineaPagoRef = useRef(null);

    // Foco al combobox visible del select de tipo de pago (el inputRef de MUI
    // apunta al input oculto y el foco ahí es invisible).
    const focusTipoPago = (delay = 0) => {
        setTimeout(() => {
            const combo = lineaPagoRef.current?.querySelector?.('[role="combobox"]');
            if (combo) {
                combo.focus();
                return;
            }
            const input = tipoPagoInputRef?.current;
            input?.focus?.();
        }, delay);
    };

    // Navegabilidad con flechas en la línea de captura: der/izq saltan entre
    // componentes (en textos solo al estar al inicio/fin para no romper la
    // edición), abajo salta a la tabla al primer monto abono y arriba al
    // campo previo. Todo salto aplica focus+select. nextRef/prevRef admiten
    // 'TABLA' y 'BOTON'. Con soloHorizontales se ignoran arriba/abajo
    // (para el select de tipo de pago, que los usa para abrir el menú).
    const irARef = (dest) => {
        if (!dest) return;
        if (dest === 'TABLA') {
            if (filas.length > 0) focusAbonoByKey(filas[0].rowKey);
            return;
        }
        if (dest === 'BOTON') {
            submitButtonRef.current?.focus?.();
            return;
        }
        if (dest === 'TIPOPAGO') {
            focusTipoPago();
            return;
        }
        focusAndSelect(dest);
    };

    const handleFlechasLinea = (e, prevRef, nextRef, soloHorizontales = false) => {
        const k = e.key;
        const esFlecha = k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowDown' || k === 'ArrowUp';
        if (!esFlecha || e.defaultPrevented) return;
        if (soloHorizontales && (k === 'ArrowDown' || k === 'ArrowUp')) return;
        const el = e.target;
        const esTexto = !!el && typeof el.selectionStart === 'number';
        const inicio = esTexto ? (el.selectionStart ?? 0) : 0;
        const fin = esTexto ? (el.selectionEnd ?? 0) : 0;
        const largo = esTexto ? String(el.value ?? '').length : 0;
        if (k === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            irARef('TABLA');
            return;
        }
        if (k === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            irARef(prevRef);
            return;
        }
        if (k === 'ArrowRight' && (!esTexto || (inicio === largo && fin === largo))) {
            e.preventDefault();
            e.stopPropagation();
            irARef(nextRef);
            return;
        }
        if (k === 'ArrowLeft' && (!esTexto || (inicio === 0 && fin === 0))) {
            e.preventDefault();
            e.stopPropagation();
            irARef(prevRef);
            return;
        }
    };

    const handleFormKeyDown = (e) => {
        // El Enter nunca dispara el submit nativo: solo navega. La única vía
        // para generar el apartado es el botón (clic / Enter o Espacio sobre
        // él) o Alt+Fin.
        if (e.key === "Enter" && !(e.target && e.target.tagName === "BUTTON")) {
            e.preventDefault();
        }
    };

    // Enter/Tab avanzan, Shift+Tab retrocede. nextRef/prevRef admiten refs,
    // 'TIPOPAGO', 'TABLA', 'BOTON' o null (null = primer abono de la tabla).
    const handleHeaderNavigation = (e, nextRef, prevRef = null) => {
        if (e.key !== "Enter" && e.key !== "Tab") return;
        if (e.key === "Tab" && e.shiftKey && (prevRef === null || prevRef === undefined)) return;
        e.stopPropagation();
        e.preventDefault();

        if (e.key === "Tab" && e.shiftKey) {
            irARef(prevRef);
            return;
        }

        if (typeof nextRef === 'string') {
            irARef(nextRef);
            return;
        }
        if (nextRef) {
            focusAndSelect(nextRef);
            return;
        }

        if (filas.length > 0) {
            focusAbonoByKey(filas[0].rowKey);
        }
    };

    const handleMontoAbonoKeyDown = (e, rowKey) => {
        const k = e.key;
        const esFlecha = k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown';
        if (esFlecha && !e.defaultPrevented) {
            const el = e.target;
            const inicio = el?.selectionStart ?? 0;
            const fin = el?.selectionEnd ?? 0;
            const largo = String(el?.value ?? '').length;
            const currentIndex = filas.findIndex((f) => f.rowKey === rowKey);
            const cadenaAtras = refSerialVisible() ?? efectivoInputRef;
            const irAtras = () => {
                if (currentIndex > 0) focusAbonoByKey(filas[currentIndex - 1].rowKey);
                else focusAndSelect(cadenaAtras);
            };
            const irAdelante = () => {
                if (currentIndex >= 0 && currentIndex + 1 < filas.length) focusAbonoByKey(filas[currentIndex + 1].rowKey);
                else submitButtonRef.current?.focus?.();
            };
            if (k === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); irAdelante(); return; }
            if (k === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); irAtras(); return; }
            if (k === 'ArrowRight' && inicio === largo && fin === largo) { e.preventDefault(); e.stopPropagation(); irAdelante(); return; }
            if (k === 'ArrowLeft' && inicio === 0 && fin === 0) { e.preventDefault(); e.stopPropagation(); irAtras(); return; }
            return;
        }
        if (k !== "Enter" && k !== "Tab") return;
        e.stopPropagation();
        e.preventDefault();

        const currentIndex = filas.findIndex((f) => f.rowKey === rowKey);
        if (k === "Tab" && e.shiftKey) {
            if (currentIndex > 0) focusAbonoByKey(filas[currentIndex - 1].rowKey);
            else focusAndSelect(refSerialVisible() ?? efectivoInputRef);
            return;
        }
        const nextIndex = currentIndex + 1;

        if (nextIndex >= 0 && nextIndex < filas.length) {
            focusAbonoByKey(filas[nextIndex].rowKey);
            return;
        }

        // Último renglón: el Enter lleva al botón; solo el botón / Alt+Fin finaliza.
        submitButtonRef.current?.focus?.();
    };

    // Atajos tal cual el modal de cobro de abonos: Alt+Fin genera, Escape cancela
    // (MUI cierra con Escape vía onClose). Enter solo navega. El listener va en
    // fase de captura para que sea global al modal: ningún stopPropagation de
    // insumos intermedios (formato numérico, autocompletados) lo puede bloquear.
    useEffect(() => {
        if (!internalOpen) return;

        const handleKeyDown = (event) => {
            if (event.altKey && (event.key === "End" || event.code === "End")) {
                event.preventDefault();
                event.stopPropagation();
                // Sin gate silencioso: el submit valida y avisa qué falta.
                submitFnRef.current?.();
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 'e') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('EFECTIVO');
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 'c') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('TARJETA CRÉDITO');
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 'd') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('TARJETA DÉBITO');
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 'q') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('CHEQUE');
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 'v') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('VALES');
                return;
            }
            if (event.altKey && event.key && event.key.toLowerCase() === 't') {
                event.preventDefault();
                event.stopPropagation();
                handleChangeTipoPago('TRANSFERENCIA');
                return;
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [internalOpen]);

    const handleObtenerListaConceptosSistema = async () => {
        const response = await GetRequest({
            url: apiConceptosYGruposList,
            loader: "Obteniendo listado de conceptos del sistema..."
        });

        if (response.hasError) {
            handleOpenAlert(response.message);
            return;
        }

        const listaX = response?.result || [];
        setListaConceptos(listaX);

        const listaConceptosAgrupadosX = listaX.reduce((acc, item) => {
            const { concepto } = item;
            if (!acc[concepto]) {
                acc[concepto] = item; // Guarda solo el primer elemento único
            }
            return acc;
        }, {});

        const conceptosAgrupadosArray = Object.values(listaConceptosAgrupadosX); //Esto convierte a un array el resultado

        setListaConceptosAgrupados(conceptosAgrupadosArray);
    };

    const handleCargarGruposPorConcepto = (conceptoSeleccionado) => {
        if (!conceptoSeleccionado) return;

        // Buscar los elementos que coincidan con el concepto seleccionado
        const listaGruposX = listaConceptos
            .filter(item => item.concepto && item.concepto.trim() && conceptoSeleccionado && conceptoSeleccionado.trim() &&
                        item.concepto.trim().includes(conceptoSeleccionado.trim())); // Filtrar por concepto

        const listaGruposAgrupadosX = listaGruposX.reduce((acc, item) => {
            const { grupo } = item;
            if (grupo && grupo.trim() && !acc[grupo]) {
                acc[grupo] = item; // Guarda solo el primer elemento único
            }
            return acc;
        }, {});

        const gruposArray = Object.values(listaGruposAgrupadosX);

        // Actualizar el estado con los grupos encontrados
        setListaGrupos(gruposArray);
    };

    const listaConceptosByOrder = listaConceptosAgrupados.slice().sort((a, b) => a.concepto.localeCompare(b.concepto));

    const listaGruposByOrder = listaGrupos.slice().sort((a, b) => a.grupo.localeCompare(b.grupo));

    useEffect(() => {
        handleCargarGruposPorConcepto(detalles.concepto);
    }, [detalles.concepto]);

    useEffect(() => {
        if (listaConceptos.length === 1 && openModalJalarPreventaCotizacion) {
            handleObtenerListaConceptosSistema()
        }
    }, [openModalJalarPreventaCotizacion]);

    const tipoPagoIcons = [
        { tipo: 'EFECTIVO', icon: <AttachMoney sx={{ fontSize: 30 }} />, tip: 'Pago en efectivo (Alt+E)' },
        { tipo: 'TARJETA CRÉDITO', icon: <CreditCard sx={{ fontSize: 30 }} />, tip: 'Pago con tarjeta crédito (Alt+C)' },
        { tipo: 'TARJETA DÉBITO', icon: <CreditCard sx={{ fontSize: 30 }} />, tip: 'Pago con tarjeta débito (Alt+D)' },
        { tipo: 'CHEQUE', icon: <AccountBalanceWallet sx={{ fontSize: 30 }} />, tip: 'Pago con cheque (Alt+Q)' },
        { tipo: 'VALES', icon: <CallToAction sx={{ fontSize: 30 }} />, tip: 'Pago con vales (Alt+V)' },
        { tipo: 'TRANSFERENCIA', icon: <AccountBalance sx={{ fontSize: 30 }} />, tip: 'Pago mediante transferencia (Alt+T)' },
    ];

    return (
        <Modal
            open={internalOpen}
            onClose={handleCloseModalGenerarApartados}
            aria-labelledby="menu-modal-title"
            aria-describedby="menu-modal-description"
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, ...modalVisibleStyle, paddingBottom: 1 }}>
                <form onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    <div className="containerPutApartados">
                        <Autocomplete
                            value={detalles.concepto}
                            onChange={(event, newValue) => {
                                setDetalles(prev => ({ ...prev, concepto: newValue }));
                                setInputConcepto(newValue || "");
                            }}
                            onInputChange={(event, newInputValue) => {
                                setInputConcepto(newInputValue);
                                setDetalles(prev => ({ ...prev, concepto: newInputValue }));
                            }}
                            inputValue={inputConcepto}
                            options={listaConceptosByOrder.map(concepto => concepto.concepto)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={conceptoInputRef}
                                    autoComplete="off"
                                    label="Conceptos de apartado:"
                                    required
                                    error={intentoEnviar && !conceptoOk}
                                    helperText={intentoEnviar && !conceptoOk ? "Obligatorio para apartar" : ""}
                                    onKeyDown={(e) => { handleFlechasLinea(e, null, grupoInputRef); handleHeaderNavigation(e, grupoInputRef, null); }}
                                    sx={{ width: '240px' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            freeSolo
                        />
                        <Autocomplete
                            value={detalles.grupo}
                            onChange={(event, newValue) => {
                                setDetalles(prev => ({ ...prev, grupo: newValue }));
                                setInputGrupo(newValue || "");
                            }}
                            onInputChange={(event, newInputValue) => {
                                setInputGrupo(newInputValue);
                                setDetalles(prev => ({ ...prev, grupo: newInputValue }));
                            }}
                            inputValue={inputGrupo}
                            options={listaGruposByOrder.map(grupo => grupo.grupo)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={grupoInputRef}
                                    autoComplete="off"
                                    label="Grupos por concepto:"
                                    onKeyDown={(e) => { handleFlechasLinea(e, conceptoInputRef, nombreClienteInputRef); handleHeaderNavigation(e, nombreClienteInputRef, conceptoInputRef); }}
                                    sx={{ width: '240px' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            freeSolo
                        />
                        <TextField
                            inputRef={nombreClienteInputRef}
                            name="nombrecliente"
                            label="Nombre cliente:"
                            required
                            error={intentoEnviar && !nombreOk}
                            helperText={intentoEnviar && !nombreOk ? "Obligatorio para apartar" : ""}
                            value={detalles.nombrecliente ? detalles.nombrecliente : ""}
                            onChange={handleChangeDetalles}
                            onKeyDown={(e) => { handleFlechasLinea(e, grupoInputRef, telefonoClienteInputRef); handleHeaderNavigation(e, telefonoClienteInputRef, grupoInputRef); }}
                            sx={{ width: '240px' }}
                            autoComplete="off"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            inputRef={telefonoClienteInputRef}
                            name="telefonocliente"
                            label="Teléfono cliente:"
                            value={detalles.telefonocliente ? detalles.telefonocliente : ""}
                            onChange={handleChangeDetalles}
                            onKeyDown={(e) => { handleFlechasLinea(e, nombreClienteInputRef, 'TIPOPAGO'); handleHeaderNavigation(e, 'TIPOPAGO', nombreClienteInputRef); }}
                            sx={{ width: '240px' }}
                            autoComplete="off"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                    <div className="venta-detalles-container">
                        <div className="detalle-item">
                            <span className="detalle-text">Total de la cuenta:</span> {NumberFormat$(totalCuenta)}
                            <span className="detalle-text">Total columna abonos:</span> {NumberFormat$(totalColumnaAbonos)}
                            <span className="detalle-text">Capital recibido:</span> {NumberFormat$(totalCapitalRecibido)}
                            <span className="detalle-text">Diferencia:</span> {NumberFormat$(diferencia)}
                        </div>
                    </div>
                    <div className="containerCobroVentasMenu" style={{ height: 'calc(15vh - 1cm)', padding: '.15rem', overflow: 'hidden' }}>
                        {tipoPagoIcons.map((t) => (
                            <Tooltip key={t.tipo} title={t.tip}>
                                <IconButton
                                    onClick={() => handleChangeTipoPago(t.tipo)}
                                    color={pago.tipoPago === t.tipo ? "primary" : "default"}
                                >
                                    {t.icon}
                                </IconButton>
                            </Tooltip>
                        ))}
                    </div>
                    <div className="containerCobroVentas">
                        <div ref={lineaPagoRef} className="actionSectionCobroVenta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField autoComplete="off"
                                name="idCobroTipoPago"
                                label="Tipo de pago:"
                                value={pago.tipoPago ? pago.tipoPago : ""}
                                onChange={(e) => handleChangeTipoPago(String(e.target.value))}
                                onKeyDown={(e) => handleFlechasLinea(e, telefonoClienteInputRef, pago.tipoPago !== "EFECTIVO" ? otroMontoInputRef : efectivoInputRef, true)}
                                inputRef={tipoPagoInputRef}
                                sx={{ width: '250px', margin: '.5rem .3rem' }}
                                select
                            >
                                <MenuItem value={1}>--Seleccionar--</MenuItem>
                                {TIPOS_PAGO.map((x) => (
                                    <MenuItem key={x} value={x}>{x}</MenuItem>
                                ))}
                            </TextField>
                            {pago.tipoPago !== "EFECTIVO" && (
                                <TextField
                                    name="MontoTipoPago"
                                    label="Monto tipo pago:"
                                    value={pago.montoTipoPago}
                                    onChange={handleChangeMontoPago("montoTipoPago")}
                                    onFocus={(e) => e.target.select()}
                                    onKeyDown={(e) => { handleFlechasLinea(e, 'TIPOPAGO', efectivoInputRef); handleHeaderNavigation(e, efectivoInputRef, 'TIPOPAGO'); }}
                                    autoComplete="off"
                                    inputRef={otroMontoInputRef}
                                    InputProps={{
                                        inputComponent: NumericFormatCustom,
                                    }}
                                    type="text"
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            <TextField
                                name="MontoEfectivo"
                                label="Monto efectivo:"
                                value={pago.montoEfectivo}
                                onChange={handleChangeMontoPago("montoEfectivo")}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => { const prevMonto = pago.tipoPago !== "EFECTIVO" ? otroMontoInputRef : 'TIPOPAGO'; const sigMonto = refSerialVisible() ?? 'TABLA'; handleFlechasLinea(e, prevMonto, sigMonto); handleHeaderNavigation(e, sigMonto, prevMonto); }}
                                autoComplete="off"
                                inputRef={efectivoInputRef}
                                InputProps={{
                                    inputComponent: NumericFormatCustom,
                                }}
                                type="text"
                                sx={{ margin: '.5rem .3rem', width: '250px' }}
                            />
                            {(pago.tipoPago === 'TARJETA DÉBITO' || pago.tipoPago === 'TARJETA CRÉDITO') && (
                                <TextField autoComplete="off"
                                    name="DatosTarjeta"
                                    label="4 dígitos tarjeta:"
                                    value={pago.digitosTarjeta}
                                    onChange={(e) => setPago((prev) => ({ ...prev, digitosTarjeta: e.target.value }))}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={digitosInputRef}
                                    onKeyDown={(e) => { handleFlechasLinea(e, efectivoInputRef, 'TABLA'); handleHeaderNavigation(e, 'TABLA', efectivoInputRef); }}
                                    inputProps={{ maxLength: 4, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(pago.tipoPago === 'CHEQUE') && (
                                <TextField autoComplete="off"
                                    name="DatosCheque"
                                    label="Datos cheque:"
                                    value={pago.datosCheque}
                                    onChange={(e) => setPago((prev) => ({ ...prev, datosCheque: e.target.value }))}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={chequeInputRef}
                                    onKeyDown={(e) => { handleFlechasLinea(e, efectivoInputRef, 'TABLA'); handleHeaderNavigation(e, 'TABLA', efectivoInputRef); }}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(pago.tipoPago === 'VALES') && (
                                <TextField autoComplete="off"
                                    name="DatosVales"
                                    label="Datos vales:"
                                    value={pago.datosVale}
                                    onChange={(e) => setPago((prev) => ({ ...prev, datosVale: e.target.value }))}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={valesInputRef}
                                    onKeyDown={(e) => { handleFlechasLinea(e, efectivoInputRef, 'TABLA'); handleHeaderNavigation(e, 'TABLA', efectivoInputRef); }}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(pago.tipoPago === 'TRANSFERENCIA') && (
                                <TextField autoComplete="off"
                                    name="DatosTransferencia"
                                    label="Datos transferencia:"
                                    value={pago.datosTransferencia}
                                    onChange={(e) => setPago((prev) => ({ ...prev, datosTransferencia: e.target.value }))}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={transferenciaInputRef}
                                    onKeyDown={(e) => { handleFlechasLinea(e, efectivoInputRef, 'TABLA'); handleHeaderNavigation(e, 'TABLA', efectivoInputRef); }}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="containerPassword" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell>Medida</TableCell>
                                        <TableCell>Costo venta</TableCell>
                                        <TableCell>Cantidad</TableCell>
                                        <TableCell>Total partida</TableCell>
                                        <TableCell>Almacén</TableCell>
                                        <TableCell>Monto abono</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filas.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography>No hay partidas en la venta para apartar</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filas.map((f) => (
                                        <TableRow key={f.rowKey}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{f.descripcion}</TableCell>
                                            <TableCell>{f.medida}</TableCell>
                                            <TableCell>{NumberFormat$(Number(f.costocliente ?? f.costounitario) || 0)}</TableCell>
                                            <TableCell>{f.cantidad}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{NumberFormat$(Number(f.totalPartida) || 0)}</TableCell>
                                            <TableCell>{f.almacenexhibidor}</TableCell>
                                            <TableCell>
                                                <TextField autoComplete="off"
                                                    inputRef={(el) => { abonoInputRefs.current[f.rowKey] = el; }}
                                                    value={abonos[f.rowKey] ?? 0}
                                                    onFocus={(e) => e.target.select()}
                                                    onChange={(e) => handleEditMontoAbono(f.rowKey, e.target.value)}
                                                    onBlur={() => handleNormalizarMontoAbono(f.rowKey, f.totalPartida)}
                                                    onKeyDown={(e) => handleMontoAbonoKeyDown(e, f.rowKey)}
                                                    type="text"
                                                    InputProps={{
                                                        inputComponent: NumericFormatCustom,
                                                    }}
                                                    sx={{
                                                        width: '200px',
                                                        '& .MuiInputBase-input': {
                                                            fontWeight: 'bold',
                                                            fontSize: '1.2rem',
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                    {bloqueaPagoTotal && (
                        <Typography
                            sx={{
                                margin: '.5rem 0',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #d32f2f',
                                backgroundColor: '#fff4f4',
                                color: '#b71c1c',
                                fontWeight: 'bold',
                            }}
                        >
                            El anticipo cubre el 100% de la cuenta. En este modal solo se recibe primer abono parcial o apartado sin abonos; use el módulo de Apartados para la entrega definitiva.
                        </Typography>
                    )}
                    <div className="containerSubmit" style={{ marginTop: '1cm' }}>
                        <Button className="validarFirmaButton"
                            ref={submitButtonRef}
                            style={{ width: '240px' }}
                            onClick={handleAccionarRegistroApartado}
                            disabled={isProcessing || !validoParaGenerar}
                        >
                            {isProcessing ? "Procesando..." : "Generar apartado (Alt+Fin)"}
                        </Button>
                    </div>
                </form>
            </Box>
        </Modal>
    );
}
